import * as React from 'react';
import {createVars} from '@packages/utilities/styled';
import {renderElement, getDefinedValue} from '../../utils';
import {TagWrapperView, TagContentView, TagCloseButtonView} from './Tag.styles';
import type {ButtonPrefixSuffix} from '../Button';
import type {LinkTargets} from '../Link';
import {Text} from '../Text';
import {HStack} from '../Stack';
import {CloseDefaultIcon} from '../../icons';

import type {
  HTMLAttributes,
  SizeContextSize,
  SpringTooling,
  AsProps,
} from '../__SharedTypes';
import {useSize} from '../../themes';
import type {TruncationProps} from '../../hooks';

export type TagProps = {
  /**
   * Content for Tag.
   */
  children?: React.ReactNode;
  /**
   * Internal prop used to control the `tabIndex` of the `TagCloseButtonView`.
   */
  _closeButtonTabIndex?: number;
  /**
   * Determines the `backgroundColor` of the `Tag`
   */
  colorVariant?:
    | 'default'
    | 'primary'
    | 'green'
    | 'orange'
    | 'purple'
    | 'pink'
    | 'red'
    | 'yellow';
  /**
   * Determines the `Tag` padding
   */
  density?: 'compact' | 'comfortable';
  /**
   * The label to use for the dismiss button.
   *
   * If not provided, the default label will be "Remove" followed by the content of the `Tag` if it is a string, otherwise it will be "Remove".
   */
  dismissLabel?: string;
  /**
   * Set an href to turn your `Tag` into a link.
   */
  href?: string;
  /**
   * Whether the `Tag` should be disabled.
   */
  isDisabled?: boolean;
  /**
   * Determines if the `Tag` shows a close button and can be closed/dismissed. Set a dismiss
   * action on `onDismiss` in conjunction with setting `isDismissable` as `true`.
   */
  isDismissable?: boolean;
  /**
   * When `true`, applies Webflow's branded typography (WF Visual Sans) and
   * branded padding to the `Tag`. Fixed/non-scaling (ignores SizeContext).
   */
  isBranded?: boolean;
  /**
   * Determines if the `Tag` has rounded borders. Defaults to `false`.
   */
  isRounded?: boolean;
  /**
   * The maximum width of the `Tag`. The content will truncate based on what the `maxWidth` is set to.
   */
  maxWidth?: number | string;
  /**
   * Set an onClick to turn your `Tag` into a `button`.
   */
  onClick?: (event: React.SyntheticEvent<HTMLElement>) => void;
  /**
   * Callback function when the `CloseButton` is clicked (enabled with `hasCloseButton`).
   */
  onDismiss?: () => void;
  /**
   * Determines what Icon renders before the content.
   */
  prefix?: ButtonPrefixSuffix;
  /**
   * A string that defines the relationship between a linked resource and the current document. Use with `href` when using `Tag` as a link.
   * `rel="noopener noreferrer"` is commonly used when the link opens in a new tab with `target="_blank"`.
   */
  rel?: string;
  /**
   * @deprecated: The preferred approach is to have components inherit from their surrounding size context rather than use the `size` prop directly.
   * Size override for Tag, adjusts the font-size and padding of the Tag.
   */
  size?: SizeContextSize;
  /**
   * A string that indicates where to display the linked resource. Use with `href` when using `Tag` as a link.
   * `_self` opens the link in the same frame as it was clicked (this is default).
   * `_blank` opens the link in a new window or tab.
   */
  target?: LinkTargets;
  /**
   * The maximum number of characters to display before truncating. Used for custom truncation.
   * Requires `maxWidth` to be specified.
   *
   * If not specificed, the `Tag` will truncate to 1 line when `maxWidth` is specified.
   */
  truncationLimit?: TruncationProps['truncationLimit'];
  /**
   * The type of truncation to apply to the `Tag` when `truncationLimit` is specified.
   * Requires `maxWidth` to be specified.
   *
   * If not specificed, the `Tag` will truncate to the end of the text.
   */
  truncationPosition?: TruncationProps['truncationPosition'];
  /**
   * Determines the opacity of the Tag's background.
   */
  variant?: 'default' | 'solid';
} & AsProps &
  Omit<HTMLAttributes, 'prefix'> &
  SpringTooling;

/**
 * # Tag
 *
 * A component used to display keywords or categories in a distinct way.
 *
 * ## Usage
 *
 * ```jsx
 * import {Tag} from '@packages/systems/spring';
 *
 * const Example = () => {
 *  return <Tag>Tag text</Tag>
 * }
 * ```
 *
 * @component
 */
export const Tag = React.forwardRef<HTMLDivElement, TagProps>(
  (
    {
      as: asProp,
      children,
      colorVariant = 'default',
      density = 'comfortable',
      dismissLabel: dismissLabelProp,
      href,
      onClick,
      isBranded = false,
      isDisabled = false,
      isDismissable = false,
      isRounded = false,
      maxWidth: maxTagWidth,
      onDismiss,
      prefix,
      rel,
      size: sizeProp,
      style,
      target,
      variant = 'default',
      _closeButtonTabIndex,
      truncationLimit,
      truncationPosition,
      ...props
    }: TagProps,
    ref
  ) => {
    const [anchorRef, setAnchorRef] = React.useState<HTMLSpanElement | null>(
      null
    );
    const size = useSize(sizeProp);

    const newStyle = {
      ...style,
      maxWidth: maxTagWidth ? cleanMaxWidth(maxTagWidth) : undefined,
    } as const;

    const prefixProps = {
      size: 16,
    } as const;
    const isButton = Boolean(onClick);
    const isLink = Boolean(href);
    const asBaseElement = isLink ? 'a' : isButton ? 'button' : 'span';
    const as = getDefinedValue(asProp, asBaseElement);
    const isInteractive = asBaseElement === 'a' || asBaseElement === 'button';

    const handleOnClick = (event: React.MouseEvent<HTMLElement>) => {
      onClick && !isDisabled && onClick(event);
    };

    const truncationProps = React.useMemo(
      () =>
        maxTagWidth && truncationLimit
          ? {
              truncationLimit,
              truncationPosition,
            }
          : {
              truncateToLines: 1,
            },
      [maxTagWidth, truncationLimit, truncationPosition]
    );

    const truncationTooltipProps = React.useMemo(
      () =>
        maxTagWidth
          ? {
              anchorRef,
              _shouldNotBeTabbable: true,
            }
          : undefined,
      [maxTagWidth, anchorRef]
    );

    const dismissLabel =
      dismissLabelProp ??
      (typeof children === 'string' ? `Remove ${children}` : 'Remove');

    return (
      <TagWrapperView
        {...props}
        isBranded={isBranded}
        isRounded={isRounded}
        ref={ref}
      >
        <TagContentView
          as={as}
          colorVariant={colorVariant}
          density={density}
          // @ts-expect-error - @SPRING_CLEANING: Handle href better on this Tag that can be a span, button, or link.
          href={href || null}
          isBranded={isBranded}
          isDisabled={isDisabled}
          isDismissable={isDismissable}
          isInteractive={isInteractive}
          isRounded={isRounded}
          onClick={handleOnClick}
          ref={setAnchorRef}
          rel={rel}
          size={size}
          style={newStyle}
          target={target}
          variant={variant}
        >
          <HStack spacing={1}>
            {prefix ? renderElement(prefix, prefixProps) : null}
            <Text
              size={size}
              truncationTooltipProps={truncationTooltipProps}
              weight="normal"
              {...truncationProps}
            >
              {children}
            </Text>
          </HStack>
        </TagContentView>
        {isDismissable ? (
          <TagCloseButtonView
            colorVariant={colorVariant}
            data-size={size}
            density={density}
            Icon={CloseDefaultIcon}
            iconProps={{colorVariant: 'inherit'}}
            isBranded={isBranded}
            isDisabled={isDisabled}
            isRounded={isRounded}
            label={dismissLabel}
            onClick={onDismiss}
            size={size}
            tabIndex={_closeButtonTabIndex}
            variant={variant}
          />
        ) : null}
      </TagWrapperView>
    );
  }
);

Tag.displayName = 'Tag';
Tag.toString = TagWrapperView.toString;

export default Tag;

export const [cssVars, restyle] = createVars({
  maxWidth: 'auto',
});

const endsWithAny = (suffixes: Array<string>, string: string) => {
  return suffixes.some((suffix) => {
    return string.endsWith(suffix);
  });
};

export const cleanMaxWidth = (width: number | string) => {
  // if the prop value is a number with no units, we'll assume it should be px (this might change as we move more to
  // rems)
  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'number'.
  if (typeof width === 'number' || !isNaN(width)) return `${width}px`;

  // if the prop value is a string and has units, we don't have anything to do
  const acceptedUnits = ['px', 'rem', 'em', '%'];
  if (typeof width === 'string' && endsWithAny(acceptedUnits, width))
    return width;

  // if it's not a number and it's not a string with units, there's not much we can do with it
  return '100%';
};
